import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";

const BASE_URL = "https://api.kennapartners.com/api/v1"; // Base URL for all API calls

const Podcasts = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [filteredPodcasts, setFilteredPodcasts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // const [file, setFile] = useState(null); // <-- Removed file state
  const [successMessage, setSuccessMessage] = useState("");

  // Current podcast being operated on
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [selectedPodcastId, setSelectedPodcastId] = useState(null);

  // Form data - file_url is now a string input
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    uploaded_at: new Date().toISOString().split("T")[0],
    file_url: "", // <-- Changed to empty string
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchPodcasts();
  }, [currentPage]);

  useEffect(() => {
    filterPodcasts();
  }, [podcasts, searchTerm]);

  const fetchPodcasts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/podcasts/?page=${currentPage}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (data && data.data && Array.isArray(data.data.podcast)) {
        setPodcasts(data.data.podcast);
        setTotalItems(data.data.total_podcast || 0);
      } else {
        console.error(
          'Invalid data format. Expected an array of podcasts under "data.podcast".'
        );
        setPodcasts([]);
        setTotalItems(0);
      }
      setError("");
    } catch (error) {
      console.error("Error fetching podcasts:", error);
      setError("Failed to fetch podcasts. Please try again.");
      setPodcasts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const filterPodcasts = () => {
    let filtered = Array.isArray(podcasts) ? podcasts : [];

    if (searchTerm) {
      filtered = filtered.filter((podcast) => {
        const title = podcast.title?.toLowerCase() || "";
        const description = podcast.description?.toLowerCase() || "";

        return (
          title.startsWith(searchTerm.toLowerCase()) ||
          description.startsWith(searchTerm.toLowerCase())
        );
      });
    }

    setFilteredPodcasts(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      uploaded_at: new Date().toISOString().split("T")[0],
      file_url: "",
    });
    setError("");
  };

  const resetAllFormStates = () => {
    setCurrentPodcast(null);
    setSelectedPodcastId(null);
    setFormData({
      title: "",
      description: "",
      uploaded_at: new Date().toISOString().split("T")[0],
      file_url: "",
    });
    // setFile(null); // <-- Removed
    setError("");
  };

  const handleCreatePodcast = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.title.trim()) {
      setError("Title is required");
      setLoading(false);
      return;
    }

    if (!formData.description.trim()) {
      setError("Description is required");
      setLoading(false);
      return;
    }

    try {
      // STEP 1: Create payload with file_url as a string
      const payload = {
        title: formData.title,
        description: formData.description,
        uploaded_at: formData.uploaded_at,
        file_url: formData.file_url, // <-- Added file_url to payload
      };

      // STEP 2: Create the podcast record
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/podcasts/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      // STEP 3: (Removed file upload logic)

      // STEP 4: Final wrap-up
      setShowCreateModal(false);
      resetAllFormStates();
      // setFile(null); // <-- Removed
      setSuccessMessage("Podcast created successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      await fetchPodcasts();
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "Failed to create podcast. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPodcast = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!currentPodcast || !currentPodcast.id) {
      setError("Podcast not found");
      setLoading(false);
      return;
    }

    if (!formData.title.trim()) {
      setError("Title is required");
      setLoading(false);
      return;
    }

    if (!formData.description.trim()) {
      setError("Description is required");
      setLoading(false);
      return;
    }

    try {
      // STEP 1: Prepare payload with file_url as a string
      const payload = {
        title: formData.title,
        description: formData.description,
        uploaded_at: formData.uploaded_at,
        file_url: formData.file_url, // <-- Added file_url to payload
      };

      // STEP 2: Update the podcast record
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/podcasts/${currentPodcast.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      const updatedPodcast = data.data.podcast;

      // STEP 3: (Removed file upload logic)

      // STEP 4: Update local state
      setPodcasts((prev) =>
        prev.map((podcast) =>
          podcast.id === updatedPodcast.id ? updatedPodcast : podcast
        )
      );
      setCurrentPodcast(updatedPodcast); // Keep Details Modal in sync

      // STEP 5: Wrap up
      setShowEditModal(false);
      // setFile(null); // <-- Removed
      setSuccessMessage("Podcast updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "Failed to edit podcast. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePodcast = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/podcasts/${selectedPodcastId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setPodcasts((prev) =>
        prev.filter((podcast) => podcast.id !== selectedPodcastId)
      );
      setShowDeleteModal(false);
      setSelectedPodcastId(null);
      setError("");
      if (filteredPodcasts.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting podcast:", error);
      setError("Failed to delete podcast. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsPodcast = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/podcasts/${id}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const podcastData = data.data.podcast;

      if (!podcastData) {
        throw new Error("No podcast data found in response");
      }

      setCurrentPodcast(podcastData);
      setError("");
    } catch (error) {
      console.error("Error fetching podcast details:", error);
      setError("Failed to fetch podcast details.");
      setCurrentPodcast(null);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentPodcast(null);
    resetForm();
    // setFile(null); // <-- Removed
    setError("");
    setShowCreateModal(true);
  };

  const openEditModal = (podcast) => {
    setCurrentPodcast(podcast);
    setFormData({
      title: podcast.title || "",
      description: podcast.description || "",
      uploaded_at: podcast.uploaded_at
        ? podcast.uploaded_at.split("T")[0]
        : new Date().toISOString().split("T")[0],
      file_url: podcast.file_url || "", // <-- Set to string
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (podcastId) => {
    setSelectedPodcastId(podcastId);
    setShowDeleteModal(true);
  };

  const openDetailsModal = async (podcastId) => {
    setLoading(true);
    setShowDetailsModal(true);
    try {
      await handleDetailsPodcast(podcastId);
    } catch (error) {
      console.error("Error loading podcast details:", error);
      setError("Failed to load podcast details");
    } finally {
      setLoading(false);
    }
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setCurrentPodcast(null);
    setSelectedPodcastId(null);
    resetForm();
    // setFile(null); // <-- Removed
    setError("");
  };

  // Pagination functions
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-between items-center mt-4">
        <div>
          <p className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
              {(currentPage - 1) * itemsPerPage + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> podcasts
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded-md"
          >
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 border rounded-md ${
                currentPage === page ? "" : ""
              }`}
            >
              {page}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded-md"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <main className="workspace">
      <Breadcrumb title="Podcasts">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Podcasts</BreadcrumbItem>
      </Breadcrumb>

      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button
            className="float-right text-white"
            onClick={() => setError("")}
          >
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Podcast Management</h1>
        <div className="flex gap-2">
          <button
            onClick={() => {
              resetAllFormStates();
              setShowCreateModal(true);
            }}
            className="btn-primary"
          >
            <span className="la la-plus"></span> Add New Podcast
          </button>
          <button
            onClick={fetchPodcasts}
            className="btn-secondary"
            disabled={loading}
          >
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex gap-4">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search podcasts by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !podcasts.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading podcasts...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                  <thead>
                    <tr>
                      <th className="w-1/4">Title</th>
                      <th className="w-1/4">Uploaded At</th>
                      <th className="w-1/4">Description</th>
                      <th className="w-1/4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(filteredPodcasts) &&
                    filteredPodcasts.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8">
                          {podcasts.length === 0
                            ? "No podcasts found. Create your first podcast!"
                            : "No podcasts match your search criteria."}
                        </td>
                      </tr>
                    ) : (
                      filteredPodcasts.map((podcast) => (
                        <tr key={podcast.id}>
                          <td className="w-1/4">
                            <div className="font-medium">{podcast.title}</div>
                          </td>
                          <td className="w-1/4">
                            {podcast.uploaded_at
                              ? new Date(
                                  podcast.uploaded_at
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "-"}
                          </td>
                          <td className="w-1/4">
                            <span className="truncate block max-w-xs">
                              {podcast.description || "No description"}
                            </span>
                          </td>
                          <td className="w-1/4">
                            <div className="flex gap-1">
                              <Button
                                onClick={() => openDetailsModal(podcast.id)}
                                className="btn btn-sm btn-info"
                                title="View Details"
                              >
                                <span className="la la-eye"></span>
                              </Button>
                              <Button
                                onClick={() => openEditModal(podcast)}
                                className="btn btn-sm btn-warning"
                                title="Edit Podcast"
                              >
                                <span className="la la-edit"></span>
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => openDeleteModal(podcast.id)}
                                className="btn btn-sm btn-danger"
                                title="Delete Podcast"
                              >
                                <span className="la la-trash"></span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Create New Podcast
                  </h5>
                  <button
                    onClick={() => {
                      resetAllFormStates();
                      setShowCreateModal(false);
                    }}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleCreatePodcast}>
                  <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Basic Information
                      </h3>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Title *
                        </label>
                        <Input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Description *
                        </label>
                        <Textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows="5"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Date *
                      </label>
                      <Input
                        type="date"
                        name="uploaded_at"
                        value={formData.uploaded_at}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    {/* MODIFIED: Changed from file input to text input */}
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Podcast File URL
                      </label>
                      <Input
                        type="text"
                        name="file_url"
                        value={formData.file_url || ""}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://.../podcast.mp3"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Provide a direct link to the hosted audio file (e.g.,
                        .mp3, .wav).
                      </p>
                    </div>
                    {/* END MODIFICATION */}
                  </div>

                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Edit Podcast
                  </h5>
                  <button
                    onClick={() => {
                      resetAllFormStates();
                      setShowEditModal(false);
                    }}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleEditPodcast}>
                  <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Basic Information
                      </h3>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Title *
                        </label>
                        <Input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Description *
                        </label>
                        <Textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows="5"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Date *
                      </label>
                      <Input
                        type="date"
                        name="uploaded_at"
                        value={formData.uploaded_at}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    {/* MODIFIED: Changed from file input to text input */}
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Podcast File URL
                      </label>
                      <Input
                        type="text"
                        name="file_url"
                        value={formData.file_url || ""}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://.../podcast.mp3"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Provide a direct link to the hosted audio file (e.g.,
                        .mp3, .wav).
                      </p>
                    </div>
                    {/* END MODIFICATION */}
                  </div>

                  {error && (
                    <div className="alert-danger mb-4">
                      {error}
                      <button
                        className="float-right text-white"
                        onClick={() => setError("")}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Updating...
                        </span>
                      ) : (
                        "Update"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal (No changes) */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-lg">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Confirm Delete
                  </h5>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <div className="modal-body p-6">
                  <p className="text-gray-700">
                    Are you sure you want to delete this podcast? This action
                    cannot be undone.
                  </p>
                </div>
                <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                  <Button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeletePodcast}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal (No changes) */}
      {showDetailsModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Podcast Details
                  </h5>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <div className="modal-body p-6">
                  {loading ? (
                    <div className="text-center py-8">
                      <span className="la la-spinner la-spin text-2xl"></span>
                      <p className="mt-2">Loading podcast details...</p>
                    </div>
                  ) : currentPodcast ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Title
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900">
                            {currentPodcast.title || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Description
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900 whitespace-pre-wrap">
                            {currentPodcast.description || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Upload Date
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900">
                            {currentPodcast.uploaded_at
                              ? new Date(
                                  currentPodcast.uploaded_at
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "N/A"}
                          </p>
                        </div>
                      </div>

                      {currentPodcast.file_url && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="col-span-1">
                            <h3 className="text-sm font-medium text-gray-500">
                              Podcast File
                            </h3>
                          </div>
                          <div className="col-span-2">
                            <a
                              href={currentPodcast.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <span className="la la-file-audio mr-2"></span>
                              {currentPodcast.file_url.split("/").pop()}
                            </a>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Created
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900">
                            {new Date(
                              currentPodcast.created_at
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Last Updated
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900">
                            {new Date(
                              currentPodcast.updated_at
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-700">
                        Failed to load podcast details
                      </p>
                    </div>
                  )}
                </div>

                <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                  <Button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Podcasts;